import {
  IMachineryAccessory,
  ICreateMachineryAccessoryRequest,
  IUpdateMachineryAccessoryRequest,
  ILinkedMachineryAccessory,
  IAccessorySearchFilters,
  IAccessorySearchResponse,
} from '../interfaces/heavyDutyMachinery/IMachineryAccessory';
import { apiService } from './ApiService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { HTTP_METHODS } from '../constants/httpMethods';

// Same separate-host convention as HeavyDutyMachineryService's own heavyDutyMachineryBaseUrl.
const heavyDutyMachineryBaseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL || '';

/** A host's own accessory catalog -- see MachineryAccessoryController.GetMyAccessories. */
export const getMyMachineryAccessories = async (): Promise<IMachineryAccessory[]> => {
  const response = await apiService<IMachineryAccessory[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ACCESSORIES,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load your accessories');
};

/** Guest-facing, [AllowAnonymous] map search -- see MachineryAccessoryController.SearchAccessories. Same "all four or none" bounding-box convention as searchHeavyDutyMachineries/searchOperators. */
export const searchAccessories = async (filters: IAccessorySearchFilters): Promise<IAccessorySearchResponse> => {
  const params: Record<string, string> = { page: String(filters.page ?? 1), pageSize: String(filters.pageSize ?? 20) };
  if (filters.minLat != null && filters.maxLat != null && filters.minLng != null && filters.maxLng != null) {
    params.minLat = String(filters.minLat);
    params.maxLat = String(filters.maxLat);
    params.minLng = String(filters.minLng);
    params.maxLng = String(filters.maxLng);
  }
  if (filters.category && filters.category.length > 0) params.category = filters.category.join(',');
  if (filters.accessoryTypeCode) params.accessoryTypeCode = filters.accessoryTypeCode;
  if (filters.minPricePerDay != null) params.minPricePerDay = String(filters.minPricePerDay);
  if (filters.maxPricePerDay != null) params.maxPricePerDay = String(filters.maxPricePerDay);
  if (filters.couplerType) params.couplerType = filters.couplerType;
  if (filters.requiresHydraulics != null) params.requiresHydraulics = String(filters.requiresHydraulics);
  if (filters.machineWeightTons != null) params.machineWeightTons = String(filters.machineWeightTons);
  if (filters.minWorkingWidthMm != null) params.minWorkingWidthMm = String(filters.minWorkingWidthMm);
  if (filters.maxWorkingWidthMm != null) params.maxWorkingWidthMm = String(filters.maxWorkingWidthMm);
  if (filters.minCapacityLiters != null) params.minCapacityLiters = String(filters.minCapacityLiters);
  if (filters.maxCapacityLiters != null) params.maxCapacityLiters = String(filters.maxCapacityLiters);
  if (filters.minOwnWeightKg != null) params.minOwnWeightKg = String(filters.minOwnWeightKg);
  if (filters.maxOwnWeightKg != null) params.maxOwnWeightKg = String(filters.maxOwnWeightKg);

  const response = await apiService<IAccessorySearchResponse>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ACCESSORIES_SEARCH,
    method: HTTP_METHODS.GET,
    params,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to search accessories');
};

export const createMachineryAccessory = async (request: ICreateMachineryAccessoryRequest): Promise<IMachineryAccessory> => {
  const response = await apiService<IMachineryAccessory>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ACCESSORIES,
    method: HTTP_METHODS.POST,
    body: { ...request },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to create accessory');
};

/** Also how a host deactivates one (request.isActive = false) -- see IUpdateMachineryAccessoryRequest's own doc comment. */
export const updateMachineryAccessory = async (id: string, request: IUpdateMachineryAccessoryRequest): Promise<IMachineryAccessory> => {
  const response = await apiService<IMachineryAccessory>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ACCESSORY_BY_ID(id),
    method: HTTP_METHODS.PUT,
    body: { ...request },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to update accessory');
};

/** Real delete -- rejected by the backend with a friendly message if the accessory is still linked to a machine or has booking history; unlink and deactivate it instead (see updateMachineryAccessory). */
export const deleteMachineryAccessory = async (id: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ACCESSORY_BY_ID(id),
    method: HTTP_METHODS.DELETE,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to delete accessory');
};

/** The accessories linked to one specific machine -- owner-only management view (can include a link to a since-deactivated accessory), NOT the guest-facing checkout list (see HeavyDutyMachineryService.getAvailableAccessories for that). */
export const getAccessoriesForMachinery = async (machineryId: string): Promise<ILinkedMachineryAccessory[]> => {
  const response = await apiService<ILinkedMachineryAccessory[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ACCESSORIES_FOR_MACHINERY(machineryId),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load accessories for this machine');
};
